import * as React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { NotFoundResults } from './Utils';
import { Box, Collapse, IconButton, Skeleton, Typography } from '@mui/material';
import { BsChevronBarDown, BsChevronBarUp, BsChevronDown, BsChevronUp } from 'react-icons/bs';
import { Badge } from '@chakra-ui/react';

export default function StickyHeadTable({
  columns,
  rows,
  limit = 25,
  hidePagiation,
  paginationPosition = 'bottom',
  loading,
  textEmpty,
  className,
  withHistory = true,
  withHeader = true,
  dataCy,
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(limit || 25);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const [rrr, setRrr] = React.useState([]);
  React.useEffect(() => {
    if (rows) {
      hidePagiation == true
        ? setRrr(rows)
        : setRrr(rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage));
    }
  }, [rows]);

  function Row(props) {
    const { row, dataCyRowPrefix, index } = props;
    const [open, setOpen] = React.useState(false);

    return (
      <React.Fragment>
        <TableRow
          onClick={row?.handleClick}
          sx={{ '& > *': !row?.history ? {} : { borderBottom: 'unset', margin: 0, padding: 0 } }}
          {...(dataCyRowPrefix && { 'data-cy': `${dataCyRowPrefix}-row-${index}` })}
        >
          {withHistory ? (
            row?.history && (
              <TableCell>
                <IconButton
                  aria-label="expand row"
                  size="small"
                  onClick={(e) => {
                    e?.stopPropagation();
                    setOpen(!open);
                  }}
                  // Vous pourriez ajouter un data-cy pour l'icône d'expansion si nécessaire
                  // data-cy={`${dataCyRowPrefix}-row-${row?.datas?.N}-expand-button`}
                >
                  {open ? <BsChevronUp color="green" /> : <BsChevronDown color="green" />}
                </IconButton>
              </TableCell>
            )
          ) : (
            <TableCell></TableCell>
          )}

          {Object.keys(row?.datas)?.map((key, colIndex) => {
            return (
              <TableCell
                className={row?.selected ? 'active-row' : ''}
                align={columns?.find((c) => c?.id == key)?.align}
                style={{ color: row?.color }}
                // Ajout d'un data-cy pour chaque cellule si besoin
                // data-cy={`${dataCyRowPrefix}-row-${row?.datas?.N}-col-${key}`}
              >
                {row?.datas[key] ?? '--'}
              </TableCell>
            );
          })}
        </TableRow>
        {withHistory && row?.history && (
          <TableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{ margin: 1 }}>
                  <Typography variant="body1" gutterBottom component="div">
                    {row?.history?.title}
                    <Badge
                      fontSize={15}
                      rounded={5}
                      padding={1}
                      paddingInline={2}
                      colorScheme="orange"
                      color={'black'}
                      marginLeft={2}
                    >
                      {row?.history?.datas?.length}
                    </Badge>
                  </Typography>
                  <Table size="small" aria-label="purchases">
                    <TableHead>
                      <TableRow>
                        {row?.history?.header?.map((h) => {
                          return <TableCell key={h}>{h}</TableCell>; // Ajout d'une clé pour la liste
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {row?.history?.datas?.length > 0 ? (
                        row?.history?.datas?.map((historyRow, historyIndex) => (
                          <TableRow key={historyRow?.index || historyIndex}>
                            <TableCell scope="row">{historyRow?.salary}</TableCell>
                            <TableCell align="right">{historyRow?.idenmites}</TableCell>
                            <TableCell>{historyRow?.permitNumber}</TableCell>
                            <TableCell>{historyRow?.permitOptDate}</TableCell>
                            <TableCell>{historyRow?.permitDate}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <NotFoundResults title={textEmpty} />
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  }

  return (
    <Paper
      className={`bg-white ${className}`}
      sx={{ width: '100%', overflow: 'hidden', backgroundColor: loading ? 'white' : 'none' }}
    >
      <TableContainer sx={{ maxHeight: '85dvh' }}>
        {hidePagiation == true ? (
          <></>
        ) : (
          paginationPosition === 'top' && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={rrr?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              // Vous pouvez ajouter un data-cy ici pour la pagination du haut
              // data-cy={`${dataCy}-top-pagination`}
            />
          )
        )}
        <Table
          stickyHeader
          size="small"
          aria-label="collapsible table"
          {...(dataCy && { 'data-cy': dataCy })}
        >
          {withHeader && (
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth, maxWidth: column?.maxWidth }}
                    // Vous pourriez ajouter un data-cy pour les en-têtes de colonne si nécessaire
                    // data-cy={`${dataCy}-header-col-${column.id}`}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          {loading ? (
            <TableBody data-cy={dataCy ? `${dataCy}-loading-body` : undefined}>
              {Array(limit)
                .fill(0)
                ?.map((_, index) => {
                  return (
                    <TableRow key={`skeleton-row-${index}`}>
                      {columns?.map((_, colIndex) => {
                        return (
                          <TableCell key={`skeleton-cell-${index}-${colIndex}`}>
                            <Skeleton variant="rectangular" height={50} width={'100%'} />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          ) : (
            <TableBody data-cy={dataCy ? `${dataCy}-body` : undefined}>
              {rrr?.length > 0
                ? rrr.map((row, index) => {
                    return (
                      <Row
                        key={row?.name || index}
                        index={index}
                        row={row}
                        dataCyRowPrefix={dataCy}
                      />
                    );
                  })
                : rrr?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columns.length + (withHistory ? 1 : 0)}>
                        <NotFoundResults title={textEmpty} />
                      </TableCell>
                    </TableRow>
                  )}
            </TableBody>
          )}
        </Table>
        {hidePagiation == true ? (
          <></>
        ) : (
          paginationPosition === 'bottom' && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={rrr?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              // Vous pouvez ajouter un data-cy ici pour la pagination du bas
              // data-cy={`${dataCy}-bottom-pagination`}
            />
          )
        )}
      </TableContainer>
    </Paper>
  );
}
